/**
 * Etichettare l'attrezzatura: i carrelli e le cassette, non la merce.
 *
 * Un contesto suo e non una riga in `flusso.ts`, perché non è un gesto del
 * flusso: nessuna cassetta si muove, nessun ordine avanza. È manutenzione —
 * si fa il giorno che arriva la fornitura di cassette nuove, e poi più.
 *
 * **Qui c'è solo la forma.** Che un carrello `K9` non esista, che un intervallo
 * all'indietro non produca niente e che duecento etichette siano il massimo che
 * si manda a una termica in un colpo sono verità sull'attrezzatura, e stanno
 * in `@errebi/domain`. Riscriverle qui vorrebbe dire tenerne due copie
 * allineate a mano.
 */
import { z } from 'zod';
/** The letter in front of an equipment code: `C` in `C121`. */
const zLetteraAttrezzatura = z.string().regex(/^[A-Z]$/, 'una lettera maiuscola');
/**
 * `cassette: null` è esplicito e non facoltativo: chi chiede solo i carrelli lo
 * deve dire. Un campo che manca è indistinguibile da un campo dimenticato dal
 * client, e qui la differenza è fra stampare otto etichette e stamparne
 * centoquaranta.
 */
export const zEtichetteAttrezzatura = z.object({
    cassette: z
        .object({
        prefisso: zLetteraAttrezzatura,
        da: z.number().int().positive(),
        a: z.number().int().positive(),
    })
        .nullable(),
    carrelli: z.array(z.string().min(1)),
});
/* ── the registry: which crates and trolleys exist ─────────────────────── */
/**
 * The shape only. Whether a code exists, is in use or may be renamed is the
 * domain's call (`equipment/registry.ts`), and the answer is an `EsitoComando`,
 * not a 400.
 */
export const zCapienzeAttrezzatura = z.object({
    capiPerCassetta: z.number().int().min(1).max(500),
    cassettePerCarrello: z.number().int().min(1).max(20),
});
export const zVoceAttrezzatura = z.object({
    codice: z.string(),
    fuoriServizio: z.string().nullable(),
    inUso: z.boolean(),
});
export const zRegistroAttrezzatura = z.object({
    cassette: z.array(zVoceAttrezzatura),
    carrelli: z.array(zVoceAttrezzatura),
    capienze: zCapienzeAttrezzatura,
});
/** Either single codes (a new trolley) or a range (a batch of crates). */
export const zAggiungiAttrezzatura = z.union([
    z.object({ codici: z.array(z.string().min(1).max(5)).min(1).max(200) }).strict(),
    z
        .object({
        prefisso: zLetteraAttrezzatura,
        da: z.number().int().positive(),
        a: z.number().int().positive(),
    })
        .strict(),
]);
export const zRinominaAttrezzatura = z.object({ nuovo: z.string().min(1).max(5) });
export const zFuoriServizio = z.object({ nota: z.string().trim().min(1).max(200) });
//# sourceMappingURL=equipment.js.map